import {
  compareDecimalStrings,
  divideDecimals,
  isDecimalString,
  isPositiveDecimal,
  normalizeDecimal,
  percentageOfDecimal,
  roundUpDecimal,
  subtractDecimals,
} from "@/shared/utils/decimal";

/** USD, so every figure the operator reads and dictates is at cents. */
export const QUOTE_DECIMALS = 2;

/** What the desk charges today. Editable in the dialog, behind a deliberate gesture. */
export const DEFAULT_COMMISSION_PERCENT = "1.2";

/**
 * Which side of the operation the operator is holding fixed.
 *
 * - `deposit` — "the client pays X": X is what leaves the client, the commission comes out of it.
 * - `credited` — "the client wants X credited": X is what must land, so the deposit has to be larger.
 */
export type RechargeQuoteMode = "deposit" | "credited";

export type RechargeQuoteIssue = "amount_required" | "amount_invalid" | "commission_invalid";

export interface RechargeQuote {
  /** What the client sends to the holding wallet. */
  deposit: string;
  /** Coloca's cut of that deposit. */
  commission: string;
  /** What ends up credited to the organization's card wallet. */
  credited: string;
  /**
   * On a `credited` quote the deposit is rounded up to a whole cent, so what lands can exceed what was asked
   * for — by a cent or two, never less. True when it does, so the screen can say so instead of showing a
   * total that silently disagrees with the number the operator typed.
   */
  creditedExceedsTarget: boolean;
}

/**
 * A commission of 100 % or more leaves nothing to credit, and makes the `credited` inversion a division by
 * zero.
 */
function isUsableCommission(percent: string): boolean {
  return (
    isDecimalString(percent) &&
    compareDecimalStrings(percent, "0") >= 0 &&
    compareDecimalStrings(percent, "100") < 0
  );
}

/** What is wrong with the two typed values, or `null` when a quote can be produced. */
export function rechargeQuoteIssue(amount: string, commissionPercent: string): RechargeQuoteIssue | null {
  const typed = amount.trim();
  if (typed === "") return "amount_required";
  if (!isDecimalString(typed) || !isPositiveDecimal(typed)) return "amount_invalid";
  if (!isUsableCommission(commissionPercent.trim())) return "commission_invalid";
  return null;
}

/**
 * The commission charged on a deposit, and what survives it.
 *
 * Rounded **up** to the cent, the direction every fee in this product rounds (`roundUpDecimal`): a quote may
 * promise the organization a little less than it gets, never a little more. `credited` is then the exact
 * subtraction, so the three figures always add up on screen — which is the only way an operator can check one
 * against a bank statement.
 */
function splitDeposit(deposit: string, commissionPercent: string) {
  const commission = roundUpDecimal(percentageOfDecimal(deposit, commissionPercent), QUOTE_DECIMALS);
  return { commission, credited: subtractDecimals(deposit, commission) };
}

/**
 * Prices one recharge of a card funding wallet. Pure display arithmetic — nothing is reserved, quoted
 * server-side or persisted; the operator reads the figures, the client deposits, and the credit is then
 * registered by hand on this same screen.
 *
 * The two modes are **exact inverses**, and deliberately so: `credited` solves `deposit × (1 − f) = target`
 * for the deposit — `target ÷ (1 − f)` — and then runs the `deposit` mode's own arithmetic on the result, so
 * feeding either answer back into the other mode reproduces the figure it came from. Adding the commission to
 * the target instead (`target + target × f`) is the tempting shortcut and is wrong: the commission is charged
 * on what is deposited, not on what is asked for, so that deposit lands short — by 14 cents on 1.000 USD at
 * 1,2 %, and proportionally more as the amount grows.
 *
 * Returns `null` when `rechargeQuoteIssue` has something to say about the inputs.
 */
export function quoteRecharge(
  amount: string,
  commissionPercent: string,
  mode: RechargeQuoteMode,
): RechargeQuote | null {
  if (rechargeQuoteIssue(amount, commissionPercent) !== null) return null;

  const typed = amount.trim();
  const percent = commissionPercent.trim();

  if (mode === "deposit") {
    // Normalized, not echoed: all three figures then leave as canonical 8-dp strings, so a caller can add or
    // compare them without wondering which one is still in the shape the operator typed.
    const deposit = normalizeDecimal(typed);
    const { commission, credited } = splitDeposit(deposit, percent);
    return { deposit, commission, credited, creditedExceedsTarget: false };
  }

  // The share of a deposit that survives the commission. Exact at 8 dp whatever the percentage: it carries at
  // most 4 fraction digits, so `(100 − f) / 100` never needs more than 6.
  const retainedFraction = divideDecimals(subtractDecimals("100", percent), "100");
  const deposit = roundUpDecimal(divideDecimals(typed, retainedFraction, "up"), QUOTE_DECIMALS);
  const { commission, credited } = splitDeposit(deposit, percent);

  return {
    deposit,
    commission,
    credited,
    creditedExceedsTarget: compareDecimalStrings(credited, typed) > 0,
  };
}
